import { Alert, Button, Card, Col, Empty, List, Row, Space, Tag, Typography } from 'antd'
import {
  DashboardOutlined,
  FileTextOutlined,
  CloudServerOutlined,
  CommentOutlined,
  WalletOutlined,
  ArrowRightOutlined,
  PlusOutlined,
  EuroCircleOutlined,
  ShoppingOutlined,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'

import apiClient from '../../api/client'
import PageHeader from '../../components/PageHeader'

interface Announcement {
  id: number
  title: string
  body: string
  severity: 'info' | 'warning' | 'critical'
}

interface Invoice {
  id: number
  invoice_no?: string
  status: string
  currency: string
  total_gross: string
  due_date?: string
  paid_at?: string
}

interface Service {
  subscription_id: number
  product_name?: string
  status: string
  billing_cycle: string
  unit_price: string
  currency: string
  next_due_date?: string
}

interface Ticket {
  id: number
  subject: string
  status: string
  created_at: string
  updated_at: string
}

interface WalletBalance {
  currency: string
  balance: string
}

function statusColor(status: string) {
  switch (status) {
    case 'active': return 'success'
    case 'suspended': return 'error'
    case 'paused': return 'warning'
    case 'cancelled': return 'default'
    default: return 'processing'
  }
}

function statusLabel(status: string, t: TFunction) {
  const map: Record<string, string> = {
    active: t('portalDashboard.statusActive', 'Aktiv'),
    suspended: t('portalDashboard.statusSuspended', 'Gesperrt'),
    paused: t('portalDashboard.statusPaused', 'Pausiert'),
    cancelled: t('portalDashboard.statusCancelled', 'Gekündigt'),
    pending: t('portalDashboard.statusPending', 'Ausstehend'),
  }
  return map[status] || status
}

function ticketStatusLabel(status: string, t: TFunction) {
  const map: Record<string, string> = {
    open: t('portalDashboard.ticketOpen', 'Offen'),
    pending: t('portalDashboard.ticketPending', 'Wartend'),
    resolved: t('portalDashboard.ticketResolved', 'Gelöst'),
    closed: t('portalDashboard.ticketClosed', 'Geschlossen'),
  }
  return map[status] || status
}

function ticketStatusColor(status: string) {
  switch (status) {
    case 'open': return 'blue'
    case 'pending': return 'orange'
    case 'resolved': return 'green'
    case 'closed': return 'default'
    default: return 'default'
  }
}

function cycleShort(cycle: string, t: TFunction) {
  const map: Record<string, string> = {
    monthly: t('portalDashboard.cycleMonthly', '/Monat'),
    yearly: t('portalDashboard.cycleYearly', '/Jahr'),
    quarterly: t('portalDashboard.cycleQuarterly', '/Quartal'),
    weekly: t('portalDashboard.cycleWeekly', '/Woche'),
    daily: t('portalDashboard.cycleDaily', '/Tag'),
    one_time: t('portalDashboard.cycleOneTime', 'einmalig'),
    biennial: t('portalDashboard.cycleBiennial', '/2 Jahre'),
    triennial: t('portalDashboard.cycleTriennial', '/3 Jahre'),
    quadrennial: t('portalDashboard.cycleQuadrennial', '/4 Jahre'),
    quinquennial: t('portalDashboard.cycleQuinquennial', '/5 Jahre'),
    sexennial: t('portalDashboard.cycleSexennial', '/6 Jahre'),
    hourly: t('portalDashboard.cycleHourly', '/Std.'),
    per_minute: t('portalDashboard.cyclePerMinute', '/Min.'),
  }
  return map[cycle] || `/${cycle}`
}

function fmtDate(iso?: string) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

export default function PortalDashboard() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const me = useQuery({
    queryKey: ['portal-me'],
    queryFn: async () =>
      (await apiClient.get<{ data: { customer: { company?: string; first_name?: string; last_name?: string; email?: string } } }>('/api/v1/portal/me')).data.data,
  })

  const invoicesQ = useQuery({
    queryKey: ['portal-invoices-dash'],
    queryFn: async () =>
      (await apiClient.get<{ data: { items: Invoice[]; total: number } }>('/api/v1/portal/invoices?limit=50')).data.data,
    staleTime: 30_000,
  })

  const servicesQ = useQuery({
    queryKey: ['portal-services-dash'],
    queryFn: async () =>
      (await apiClient.get<{ data: Service[] }>('/api/v1/portal/services')).data.data,
    staleTime: 60_000,
  })

  const ticketsQ = useQuery({
    queryKey: ['portal-tickets-dash'],
    queryFn: async () =>
      (await apiClient.get<{ data: { items: Ticket[]; total: number } }>('/api/v1/portal/tickets?limit=5&status=open')).data.data,
    staleTime: 30_000,
  })

  const walletQ = useQuery({
    queryKey: ['portal-wallet-dash'],
    queryFn: async () =>
      (await apiClient.get<{ data: { balances: WalletBalance[] } }>('/api/v1/portal/wallet')).data.data,
    staleTime: 60_000,
  })

  // Payment-method status — nudge PayPal customers who haven't set up an
  // off-session agreement yet (otherwise their invoices can't be auto-collected).
  const payQ = useQuery({
    queryKey: ['portal-paymethods-dash'],
    queryFn: async () =>
      (await apiClient.get<{ data: { default: string; paypal_agreement: boolean } }>('/api/v1/portal/payment-methods/manage')).data.data,
    staleTime: 60_000,
  })
  const needsPaypalAgreement = payQ.data?.default === 'paypal' && !payQ.data?.paypal_agreement

  const announcementsQ = useQuery({
    queryKey: ['portal-announcements'],
    queryFn: async () =>
      (await apiClient.get<{ data: Announcement[] }>('/api/v1/portal/announcements/active')).data.data,
    staleTime: 5 * 60_000,
  })

  // Reseller contracts — only resolves for active resellers (403 otherwise, which
  // React Query leaves as undefined → no alert for regular customers).
  const contractsQ = useQuery({
    queryKey: ['portal-reseller-contracts-dash'],
    queryFn: async () =>
      (await apiClient.get<{ data: { slug: string; title: string; signed: boolean }[] }>('/api/v1/portal/reseller/contracts')).data.data,
    retry: false,
    staleTime: 60_000,
  })
  const unsignedContracts = (contractsQ.data || []).filter(c => !c.signed)

  const customer = me.data?.customer
  const displayName =
    customer?.company ||
    [customer?.first_name, customer?.last_name].filter(Boolean).join(' ') ||
    customer?.email || ''

  const allInvoices = invoicesQ.data?.items ?? []
  const openInvoices = allInvoices.filter(i => !i.paid_at && (i.status === 'issued' || i.status === 'sent'))
  const openAmount = openInvoices.reduce((sum, i) => sum + parseFloat(i.total_gross || '0'), 0)
  const openCurrency = openInvoices[0]?.currency || 'EUR'

  const services = servicesQ.data ?? []
  const activeServices = services.filter(s => s.status === 'active').length

  const openTickets = ticketsQ.data?.items ?? []
  const openTicketCount = ticketsQ.data?.total ?? openTickets.length

  const walletBalances = (walletQ.data?.balances ?? []).filter(b => parseFloat(b.balance) > 0)

  const severityMap: Record<string, 'info' | 'warning' | 'error'> = {
    info: 'info', warning: 'warning', critical: 'error',
  }

  return (
    <>
      <PageHeader
        icon={<DashboardOutlined />}
        title={t('portal.dashboard.title', 'Übersicht')}
        subtitle={displayName}
      />

      {(announcementsQ.data || []).length > 0 && (
        <Space direction="vertical" className="w-full mb-16">
          {(announcementsQ.data || []).map(ann => (
            <Alert key={ann.id}
              type={severityMap[ann.severity] || 'info'}
              message={ann.title}
              description={ann.body}
              showIcon banner />
          ))}
        </Space>
      )}

      {unsignedContracts.length > 0 && (
        <Alert
          className="mb-16"
          type="warning"
          showIcon
          banner
          message={t('portal.dashboard.contractsUnsigned.title', 'Nicht unterzeichnete Verträge')}
          description={t('portal.dashboard.contractsUnsigned.body', 'Bitte unterzeichnen Sie {{list}}, um Ihr Partnerprogramm nutzen und Lizenzen anlegen zu können.', { list: unsignedContracts.map(c => c.title).join(', ') })}
          action={(
            <Link to="/portal/reseller">
              <Button size="small" type="primary">
                {t('portal.dashboard.contractsUnsigned.cta', 'Jetzt unterzeichnen')}
              </Button>
            </Link>
          )}
        />
      )}

      {needsPaypalAgreement && (
        <Alert
          className="mb-16"
          type="warning"
          showIcon
          banner
          message={t('portal.dashboard.paypalNeeded.title', 'PayPal-Zahlung noch nicht aktiviert')}
          description={t('portal.dashboard.paypalNeeded.body', 'Ihre Rechnungen können derzeit nicht automatisch per PayPal eingezogen werden. Bitte richten Sie einmalig Ihr PayPal-Abkommen ein, damit offene Beträge automatisch beglichen werden.')}
          action={(
            <Link to="/portal/billing?tab=payment-methods">
              <Button size="small" type="primary">
                {t('portal.dashboard.paypalNeeded.cta', 'Jetzt einrichten')}
              </Button>
            </Link>
          )}
        />
      )}

      {/* KPI strip — always 4 fixed tiles */}
      <Row gutter={[12, 12]} className="mb-16">
        <Col xs={12} sm={6}>
          <Link to="/portal/invoices" className="kpi-tile-link">
            <div className={`kpi-tile${openInvoices.length > 0 ? ' kpi-warning' : ''}`}>
              <div className="kpi-tile-label">
                <FileTextOutlined className="mr-4" />
                {t('portal.dashboard.openInvoices', 'Offene Rechnungen')}
              </div>
              <div className={`kpi-tile-value${openInvoices.length > 0 ? ' kpi-warning' : ''}`}>
                {openInvoices.length}
              </div>
            </div>
          </Link>
        </Col>
        <Col xs={12} sm={6}>
          <Link to="/portal/invoices" className="kpi-tile-link">
            <div className={`kpi-tile${openAmount > 0 ? ' kpi-warning' : ''}`}>
              <div className="kpi-tile-label">
                <EuroCircleOutlined className="mr-4" />
                {t('portal.dashboard.openAmount', 'Offener Betrag')}
              </div>
              <div className={`kpi-tile-value${openAmount > 0 ? ' kpi-warning' : ''}${openAmount >= 10000 ? ' kpi-tile-value--md' : ''}`}>
                {openAmount.toFixed(2)} {openCurrency}
              </div>
            </div>
          </Link>
        </Col>
        <Col xs={12} sm={6}>
          <Link to="/portal/services" className="kpi-tile-link">
            <div className="kpi-tile kpi-primary">
              <div className="kpi-tile-label">
                <CloudServerOutlined className="mr-4" />
                {t('portal.dashboard.activeServices', 'Aktive Services')}
              </div>
              <div className="kpi-tile-value kpi-info">{activeServices}</div>
            </div>
          </Link>
        </Col>
        <Col xs={12} sm={6}>
          <Link to="/portal/tickets" className="kpi-tile-link">
            <div className={`kpi-tile${openTicketCount > 0 ? ' kpi-info' : ''}`}>
              <div className="kpi-tile-label">
                <CommentOutlined className="mr-4" />
                {t('portal.dashboard.openTickets', 'Offene Tickets')}
              </div>
              <div className={`kpi-tile-value${openTicketCount > 0 ? ' kpi-info' : ''}`}>
                {openTicketCount}
              </div>
            </div>
          </Link>
        </Col>
      </Row>

      {/* Wallet balance — shown only when positive credit exists */}
      {walletBalances.length > 0 && (
        <div className="portal-wallet-bar">
          <WalletOutlined className="portal-wallet-bar__icon" />
          <Typography.Text className="portal-wallet-bar__label">
            {t('portal.dashboard.walletCredit', 'Verfügbares Guthaben:')}
          </Typography.Text>
          {walletBalances.map(b => (
            <Typography.Text key={b.currency} strong className="portal-wallet-bar__amount">
              {parseFloat(b.balance).toFixed(2)} {b.currency}
            </Typography.Text>
          ))}
          <Link to="/portal/billing?tab=wallet" className="portal-wallet-bar__link">
            <Button size="small" type="text">
              {t('common.view', 'Ansehen')} <ArrowRightOutlined />
            </Button>
          </Link>
        </div>
      )}

      {/* Quick actions */}
      <div className="quick-actions">
        <Button icon={<ShoppingOutlined />} onClick={() => navigate('/portal/shop')}>
          {t('portal.dashboard.quickShop', 'Service bestellen')}
        </Button>
        <Button icon={<PlusOutlined />} onClick={() => navigate('/portal/tickets?action=new')}>
          {t('portal.dashboard.quickTicket', 'Ticket erstellen')}
        </Button>
        <Button icon={<FileTextOutlined />} onClick={() => navigate('/portal/invoices')}>
          {t('portal.dashboard.quickInvoices', 'Rechnungen')}
        </Button>
        <Button icon={<WalletOutlined />} onClick={() => navigate('/portal/billing?tab=wallet')}>
          {t('portal.dashboard.quickTopup', 'Guthaben aufladen')}
        </Button>
      </div>

      {/* Main content grid */}
      <Row gutter={[16, 16]}>
        {/* Open invoices */}
        <Col xs={24} lg={12}>
          <Card
            size="small"
            title={
              <Space>
                <FileTextOutlined />
                {t('portal.dashboard.openInvoicesCard', 'Offene Rechnungen')}
              </Space>
            }
            extra={<Link to="/portal/invoices"><Button type="link" size="small">{t('common.all', 'Alle')} <ArrowRightOutlined /></Button></Link>}
          >
            {openInvoices.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t('portal.dashboard.noOpenInvoices', 'Keine offenen Rechnungen')}
                className="py-12" />
            ) : (
              <List
                size="small"
                dataSource={openInvoices.slice(0, 5)}
                renderItem={inv => (
                  <List.Item
                    actions={[
                      <Link key="view" to={`/portal/invoices/${inv.id}`}>
                        <Button size="small" type="primary">
                          {t('portal.dashboard.pay', 'Bezahlen')}
                        </Button>
                      </Link>,
                    ]}
                  >
                    <List.Item.Meta
                      title={
                        <Link to={`/portal/invoices/${inv.id}`} className="font-medium">
                          {inv.invoice_no || `#${inv.id}`}
                        </Link>
                      }
                      description={
                        <Space size={4}>
                          <Tag color="warning">{t('portal.invoice.open', 'Offen')}</Tag>
                          <Typography.Text type="secondary" className="text-sm">
                            {t('portal.dashboard.due', 'Fällig')}: {fmtDate(inv.due_date)}
                          </Typography.Text>
                        </Space>
                      }
                    />
                    <Typography.Text strong className="mr-8">
                      {parseFloat(inv.total_gross).toFixed(2)} {inv.currency}
                    </Typography.Text>
                  </List.Item>
                )}
              />
            )}
          </Card>
        </Col>

        {/* Recent tickets */}
        <Col xs={24} lg={12}>
          <Card
            size="small"
            title={
              <Space>
                <CommentOutlined />
                {t('portal.dashboard.recentTickets', 'Offene Tickets')}
              </Space>
            }
            extra={
              <Space size={4}>
                <Link to="/portal/tickets?action=new">
                  <Button type="primary" size="small" icon={<PlusOutlined />}>
                    {t('portal.dashboard.newTicket', 'Neu')}
                  </Button>
                </Link>
                <Link to="/portal/tickets">
                  <Button type="link" size="small">{t('common.all', 'Alle')} <ArrowRightOutlined /></Button>
                </Link>
              </Space>
            }
          >
            {openTickets.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t('portal.dashboard.noOpenTickets', 'Keine offenen Tickets')}
                className="py-12" />
            ) : (
              <List
                size="small"
                dataSource={openTickets.slice(0, 5)}
                renderItem={ticket => (
                  <List.Item
                    actions={[
                      <Link key="view" to={`/portal/tickets/${ticket.id}`}>
                        <Button size="small">{t('portal.dashboard.view', 'Ansehen')}</Button>
                      </Link>,
                    ]}
                  >
                    <List.Item.Meta
                      title={
                        <Link to={`/portal/tickets/${ticket.id}`} className="font-medium">
                          {ticket.subject.length > 40 ? ticket.subject.slice(0, 37) + '…' : ticket.subject}
                        </Link>
                      }
                      description={
                        <Space size={4}>
                          <Tag color={ticketStatusColor(ticket.status)} className="text-xs">
                            {ticketStatusLabel(ticket.status, t)}
                          </Tag>
                          <Typography.Text type="secondary" className="text-sm">
                            {fmtDate(ticket.updated_at)}
                          </Typography.Text>
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            )}
          </Card>
        </Col>

        {/* Active services */}
        <Col xs={24}>
          <Card
            size="small"
            title={
              <Space>
                <CloudServerOutlined />
                {t('portal.dashboard.myServices', 'Meine Services')}
              </Space>
            }
            extra={
              <Space size={4}>
                <Link to="/portal/shop">
                  <Button type="primary" size="small" icon={<PlusOutlined />}>
                    {t('portal.dashboard.orderNew', 'Bestellen')}
                  </Button>
                </Link>
                <Link to="/portal/services">
                  <Button type="link" size="small">{t('common.all', 'Alle')} <ArrowRightOutlined /></Button>
                </Link>
              </Space>
            }
          >
            {services.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t('portal.dashboard.noServices', 'Keine Services vorhanden')}
                className="py-12">
                <Button type="primary" onClick={() => navigate('/portal/shop')}>
                  {t('portal.dashboard.orderFirst', 'Ersten Service bestellen')}
                </Button>
              </Empty>
            ) : (
              <List
                size="small"
                dataSource={services.slice(0, 6)}
                renderItem={svc => (
                  <List.Item
                    actions={[
                      <Link key="detail" to={`/portal/services/${svc.subscription_id}`}>
                        <Button size="small">{t('portal.dashboard.manage', 'Verwalten')}</Button>
                      </Link>,
                    ]}
                  >
                    <List.Item.Meta
                      title={
                        <Link to={`/portal/services/${svc.subscription_id}`} className="font-medium">
                          {svc.product_name || `Service #${svc.subscription_id}`}
                        </Link>
                      }
                      description={
                        svc.next_due_date
                          ? `${t('portal.dashboard.nextDue', 'Nächste Fälligkeit')}: ${fmtDate(svc.next_due_date)}`
                          : undefined
                      }
                    />
                    <Space size={6}>
                      <Tag color={statusColor(svc.status)} className="text-xs">
                        {statusLabel(svc.status, t)}
                      </Tag>
                      <Typography.Text type="secondary" className="text-sm">
                        {parseFloat(svc.unit_price).toFixed(2)} {svc.currency}{cycleShort(svc.billing_cycle, t)}
                      </Typography.Text>
                    </Space>
                  </List.Item>
                )}
              />
            )}
          </Card>
        </Col>
      </Row>
    </>
  )
}
